import React, { useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import Button from '../components/button';
import { screenLanding1, screenLanding2, screenLanding3, screenLanding4, screenLanding5 } from '../images';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const LandingPage = () => {
  const formSectionRef = useRef(null);

  const handleButtonClick = () => {
    if (formSectionRef.current) {
      formSectionRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <PageContainer>
      <Header>
        <h1>Still Stuck in a Job That Doesn’t Pay What You're Worth?</h1>
        <p>Discover How to Become a Well-Paid Business Analyst in Under 6 Months — Even with ZERO Tech Background!</p>
      </Header>

      <Section className="info-session">
        <h2>FREE Live Info Session</h2>
        <p>Learn how over 200 people landed high-paying tech roles with no tech experience.</p>
        <p>📅 Live Session: <strong>Thursday, 8th May 2025 , 7PM EST</strong></p>
        <p>🎓 Hosted by: Coach Michael + Coach Gbemi</p>
        <StyledButton onClick={handleButtonClick}>Reserve My Free Spot Now</StyledButton>
      </Section>

      <Section>
        <h3>Is This You?</h3>
        <ul>
          <li>Feeling undervalued at work?</li>
          <li>Curious about tech but don’t want to learn to code?</li>
          <li>Ready for a career that pays $100K–$250K+?</li>
        </ul>
      </Section>

      <Section>
        <h3>What You'll Learn</h3>
        <ul>
          <li>What Business Analysts actually do (and why it’s perfect for non-techies)</li>
          <li>Our 3-step framework for landing roles in under 6 months</li>
          <li>Secrets to a job-winning resume + interview strategies</li>
          <li>Live Q&A with real coaches and recent grads</li>
          <li>How to use Artificial inteligence (AI) to land a job</li>
        </ul>
      </Section>

      <Section>
        <h3>Real Results, Real People</h3>
        <blockquote>"I went from $54K to $120K in under 6 months." — Janet O.</blockquote>
        <blockquote>"TheSkillGarage changed my life. It's not just a course — it’s a movement." — Emeka K.</blockquote>
        <p>💰 We’ve helped 200+ people land tech roles and generated over <strong>$250M in salaries</strong> for BIPOC families.</p>
        <ImageGallery>
          <img src={screenLanding1} alt="Testimonial 1" />
          <img src={screenLanding2} alt="Testimonial 2" />
          <img src={screenLanding3} alt="Testimonial 3" />
          <img src={screenLanding4} alt="Testimonial 4" />
          <img src={screenLanding5} alt="Testimonial 5" />
        </ImageGallery>
      </Section>

      <Section className="flex flex-col items-center w-full" ref={formSectionRef}>
        <h3>Save Your Spot Now</h3>
        <p className='text-sm mb-11'>Join the live session and take the first step to your tech career.</p>
        <FormContainer>
          <iframe
            aria-label="Business Analysis Free Live Session"
            title="Business Analysis Free Live Session"
            src="https://forms.zohopublic.com/info6463/form/BusinessAnalysisFreeLiveSession/formperma/Za9W_gCKCzZ_R_2zNgxv10phPkAQGwCjYoXHLccB694"
          ></iframe>
        </FormContainer>
      </Section>

      <Footer>
        &copy; 2025 TheSkillGarage. All rights reserved.
      </Footer>
    </PageContainer>
  );
};

const PageContainer = styled.div`
  background-color: #FCFCF5;
  color: #1E1B2E;
  font-family: 'Poppins', sans-serif;
  padding: 0 16px;
  animation: ${fadeIn} 1s ease-in-out;
`;

const Header = styled.header`
  background: linear-gradient(90deg, #1E1B2E, #333333);
  padding: 60px 30px;
  text-align: center;
  color: #FCFCF5;
  border-radius: 16px;
  margin-bottom: 30px;
  animation: ${fadeIn} 1.2s ease-in-out;

  h1 {
    font-size: 3rem;
    font-weight: 800;
    line-height: 1.2;
  }

  p {
    margin-top: 20px;
    font-size: 1.3rem;
    font-weight: 400;
  }
`;

const Section = styled.section`
  padding: 50px 30px;
  margin: 30px 0;
  background: #FFFFFF;
  border-radius: 16px;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
  animation: ${fadeIn} 1.4s ease-in-out;

  &.info-session {
    background: linear-gradient(135deg, #F8D210, #FFD700);
    text-align: center;
    display: flex;
    flex-direction: column;
    color: #1E1B2E;
    border: none;
  }

  h2, h3 {
    font-size: 2rem;
    margin-bottom: 20px;
    font-weight: 700;
  }

  ul {
    list-style-type: disc;
    padding-left: 30px;
  }

  li {
    margin-bottom: 14px;
    font-size: 1.1rem;
  }
`;

const FormContainer = styled.div`
  // background: linear-gradient(135deg, #F8D210, #FFD700);
  padding: 40px;
  border-radius: 16px;
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15);
  max-width: 100%;
  width: 100%;
  height: 100%;
  margin: 0 auto;
  animation: ${fadeIn} 1.6s ease-in-out;
  display: flex;
  flex-direction: column;
  align-items: center;

  iframe {
    width: 100%;
    height: 700px;
    border: none;
    border-radius: 8px;
    background: #FFFFFF;

    @media (max-width: 768px) {
      height: 850px;
      width: 100vw;
    }
  }
`;

const StyledButton = styled(Button)`
  background-color: #1E1B2E;
  color: #FCFCF5;
  padding: 14px 28px;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 12px;
  transition: background-color 0.3s, transform 0.3s;
  margin-top: 20px;
  width: fit-content;
  align-self: center;

  &:hover {
    background-color: #333333;
    transform: scale(1.1);
  }
`;

const Footer = styled.footer`
  text-align: center;
  padding: 16px;
  font-size: 0.9rem;
  color: #666666;
  animation: ${fadeIn} 1.8s ease-in-out;
`;

const ImageGallery = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  justify-content: center;
  margin-top: 20px;

  img {
    width: 100%;
    max-width: 200px;
    border-radius: 8px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    transition: transform 0.3s;

    &:hover {
      transform: scale(1.05);
    }
  }

  @media (max-width: 768px) {
    img {
      max-width: 150px;
    }
  }
`;

export default LandingPage;